import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  doc,
  getDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { Transaction } from '../types';
import { lifeCycleService } from './lifeCycle.service';
import { moldHistoryService } from './mold-history.service';
import { HistoryFormData } from '../types/history.types';

export class TransactionService {
  private readonly collectionName = 'transactions';

  // 입출고 처리
  async processTransaction(transactionData: Omit<Transaction, 'id' | 'timestamp'>): Promise<Transaction> {
    try {
      const timestamp = Timestamp.now();
      const transaction: Omit<Transaction, 'id'> = {
        ...transactionData,
        timestamp
      };

      const docRef = await addDoc(collection(db, this.collectionName), transaction);
      
      // 금형 상태 업데이트
      await this.updateMoldStatus(transactionData.moldId, transactionData.type, transactionData.shots);
      
      // 생성된 Transaction 객체 반환
      return {
        id: docRef.id,
        ...transaction
      } as Transaction;
    } catch (error) {
      console.error('입출고 처리 실패:', error);
      throw new Error('입출고 처리에 실패했습니다.');
    }
  }

  // 출고 처리
  async checkOut(moldId: string, userId: string, purpose: string, expectedShots: number, expectedReturn: Date): Promise<Transaction> {
    const transactionData: Omit<Transaction, 'id' | 'timestamp'> = {
      moldId,
      type: 'check-out',
      userId,
      userName: '',
      purpose,
      shots: 0, // 출고 시 시작 쇼트
      expectedReturn: expectedReturn
    };

    const transaction = await this.processTransaction(transactionData);

    // 이력 자동 생성
    try {
      const historyData: HistoryFormData = {
        moldId,
        type: 'outbound',
        title: '금형 출고',
        description: purpose || '출고 처리',
        date: new Date(),
        shots: expectedShots,
        purpose,
        expectedReturn
      };
      await moldHistoryService.create(historyData);
    } catch (error) {
      console.error('출고 이력 생성 실패:', error);
      // 이력 생성 실패해도 트랜잭션은 성공으로 간주
    }

    return transaction;
  }

  // 입고 처리 
  async checkIn(moldId: string, userId: string, actualShots: number, currentShots: number = 0): Promise<Transaction> {
    const transactionData: Omit<Transaction, 'id' | 'timestamp'> = {
      moldId,
      type: 'check-in',
      userId,
      userName: '',
      shots: actualShots,
      actualReturn: new Date()
    };

    // 쇼트 카운팅 업데이트
    if (actualShots > 0) {
      await lifeCycleService.updateShotCount(moldId, currentShots, actualShots);
    }

    const transaction = await this.processTransaction(transactionData);

    // 이력 자동 생성
    try {
      const historyData: HistoryFormData = {
        moldId,
        type: 'inbound',
        title: '금형 입고',
        description: `생산 완료 (${actualShots.toLocaleString()} shots)`,
        date: new Date(),
        shots: actualShots
      };
      await moldHistoryService.create(historyData);
    } catch (error) {
      console.error('입고 이력 생성 실패:', error);
      // 이력 생성 실패해도 트랜잭션은 성공으로 간주
    }

    return transaction;
  }

  // 금형 상태 업데이트
  private async updateMoldStatus(moldId: string, type: 'check-out' | 'check-in', shots?: number): Promise<void> {
    try {
      const moldRef = doc(db, 'molds', moldId);
      
      if (type === 'check-out') {
        // 출고: 상태를 'in-use'으로 변경
        await updateDoc(moldRef, {
          status: 'in-use',
          updatedAt: Timestamp.now()
        });
      } else {
        // 입고: 상태를 'stored'으로 변경, 쇼트 수 업데이트
        const updateData: any = {
          status: 'stored',
          updatedAt: Timestamp.now()
        };
        
        if (shots !== undefined) {
          updateData.currentShots = shots;
        }
        
        await updateDoc(moldRef, updateData);
      }
    } catch (error) {
      console.error('금형 상태 업데이트 실패:', error);
      throw new Error('금형 상태 업데이트에 실패했습니다.');
    }
  }

  // 입출고 히스토리 조회
  async getTransactionHistory(moldId?: string, limit: number = 50): Promise<Transaction[]> {
    try {
      let dbQuery;
      
      if (moldId) {
        dbQuery = query(
          collection(db, this.collectionName),
          where('moldId', '==', moldId),
          orderBy('timestamp', 'desc')
        );
      } else {
        dbQuery = query(
          collection(db, this.collectionName),
          orderBy('timestamp', 'desc')
        );
      }

      const querySnapshot = await getDocs(dbQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Transaction));
    } catch (error) {
      console.error('입출고 히스토리 조회 실패:', error);
      throw new Error('입출고 히스토리 조회에 실패했습니다.');
    }
  }

  // 현재 사용중인 금형 목록
  async getCurrentlyInUse(): Promise<Transaction[]> {
    try {
      const dbQuery = query(
        collection(db, this.collectionName),
        where('type', '==', 'check-out'),
        where('actualReturn', '==', null),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(dbQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Transaction));
    } catch (error) {
      console.error('사용중 금형 조회 실패:', error);
      throw new Error('사용중 금형 조회에 실패했습니다.');
    }
  }

  // 연체된 금형 목록
  async getOverdueTransactions(): Promise<Transaction[]> {
    try {
      const now = Timestamp.now();
      const dbQuery = query(
        collection(db, this.collectionName),
        where('type', '==', 'check-out'),
        where('actualReturn', '==', null),
        where('expectedReturn', '<', now),
        orderBy('expectedReturn', 'asc')
      );

      const querySnapshot = await getDocs(dbQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Transaction));
    } catch (error) {
      console.error('연체 금형 조회 실패:', error);
      throw new Error('연체 금형 조회에 실패했습니다.');
    }
  }

  // 기간별 입출고 내역 조회 (생산 실적용)
  async getTransactionsByDateRange(startDate: Date, endDate: Date): Promise<Transaction[]> {
    try {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);

      const dbQuery = query(
        collection(db, this.collectionName),
        where('timestamp', '>=', startTimestamp),
        where('timestamp', '<=', endTimestamp),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(dbQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Transaction));
    } catch (error) {
      console.error('기간별 입출고 조회 실패:', error);
      throw new Error('기간별 입출고 조회에 실패했습니다.');
    }
  }
}

export const transactionService = new TransactionService();
